/**
 * StockPhotoSearch Component
 *
 * Reusable component for searching and selecting stock photos
 * from Unsplash and Pexels.
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../services/api';

interface StockPhoto {
  id: string;
  source: 'unsplash' | 'pexels';
  type: 'photo' | 'video';
  width: number;
  height: number;
  color: string;
  description: string;
  urls: {
    thumb: string;
    small: string;
    regular: string;
    full: string;
  };
  attribution: {
    photographer: string;
    photographer_url: string;
    source_url: string;
  };
}

interface Flight {
  id: string;
  display_id: string;
  name: string;
  color: string;
}

interface Post {
  id: string;
  display_id: string;
  text: string;
  status: string;
}

type DownloadSize = 'small' | 'regular' | 'full';

// Farboptionen für Filter (von beiden APIs unterstützt)
const COLOR_OPTIONS = [
  { value: '', labelKey: 'stock.colors.all', color: 'bg-gradient-to-r from-red-500 via-green-500 to-blue-500' },
  { value: 'black_and_white', labelKey: 'stock.colors.blackAndWhite', color: 'bg-gradient-to-r from-black to-white' },
  { value: 'black', labelKey: 'stock.colors.black', color: 'bg-black' },
  { value: 'white', labelKey: 'stock.colors.white', color: 'bg-white border border-gray-300' },
  { value: 'yellow', labelKey: 'stock.colors.yellow', color: 'bg-yellow-400' },
  { value: 'orange', labelKey: 'stock.colors.orange', color: 'bg-orange-500' },
  { value: 'red', labelKey: 'stock.colors.red', color: 'bg-red-500' },
  { value: 'purple', labelKey: 'stock.colors.purple', color: 'bg-purple-500' },
  { value: 'magenta', labelKey: 'stock.colors.magenta', color: 'bg-pink-500' },
  { value: 'green', labelKey: 'stock.colors.green', color: 'bg-green-500' },
  { value: 'teal', labelKey: 'stock.colors.teal', color: 'bg-teal-500' },
  { value: 'blue', labelKey: 'stock.colors.blue', color: 'bg-blue-500' },
];

// Beliebte Suchbegriffe für Inspiration
const POPULAR_SEARCHES = [
  'Business', 'Natur', 'Menschen', 'Technologie', 'Essen', 'Reisen',
  'Fitness', 'Mode', 'Architektur', 'Tiere', 'Abstrakt', 'Minimal',
];

interface StockPhotoSearchProps {
  projectId: string;
  onSelect?: (photo: StockPhoto) => void;
  onDownload?: (mediaId: string, url: string) => void;
  allowMultiple?: boolean;
  defaultSource?: 'unsplash' | 'pexels' | 'all';
  compact?: boolean;
  flights?: Flight[];
  posts?: Post[];
}

export function StockPhotoSearch({
  projectId,
  onSelect,
  onDownload,
  defaultSource = 'all',
  compact = false,
  flights = [],
  posts = [],
}: StockPhotoSearchProps) {
  const { t } = useTranslation('media');
  // Persistente Sucheinstellungen aus sessionStorage laden
  const getInitialState = <T,>(key: string, defaultValue: T): T => {
    if (typeof window === 'undefined') return defaultValue;
    const saved = sessionStorage.getItem(`stockSearch_${key}`);
    return saved ? JSON.parse(saved) : defaultValue;
  };

  const [query, setQuery] = useState(() => getInitialState('query', ''));
  const [source, setSource] = useState<'unsplash' | 'pexels' | 'all'>(() =>
    getInitialState('source', defaultSource)
  );
  const [orientation, setOrientation] = useState<'landscape' | 'portrait' | 'square' | ''>(() =>
    getInitialState('orientation', '')
  );
  const [colorFilter, setColorFilter] = useState<string>(() => getInitialState('color', ''));
  const [photos, setPhotos] = useState<StockPhoto[]>(() => getInitialState('photos', []));
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [page, setPage] = useState(() => getInitialState('page', 1));
  const [hasMore, setHasMore] = useState(() => getInitialState('hasMore', false));
  const [availableSources, setAvailableSources] = useState<string[]>([]);
  const [selectedPhoto, setSelectedPhoto] = useState<StockPhoto | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [downloadedIds, setDownloadedIds] = useState<Set<string>>(new Set());

  // Download modal state
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [downloadPhoto, setDownloadPhoto] = useState<StockPhoto | null>(null);
  const [downloadSize, setDownloadSize] = useState<DownloadSize>('regular');
  const [selectedFlightId, setSelectedFlightId] = useState<string>('');
  const [selectedPostId, setSelectedPostId] = useState<string>('');

  // Ref für Success Message Timer
  const successTimerRef = useRef<number | null>(null);

  // Ref um initiales Laden zu erkennen (verhindert Neu-Suche bei Mount)
  const isInitialMountRef = useRef(true);

  // Autocomplete State
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [suggestions, setSuggestions] = useState<{
    user_history: { query: string; type: 'history'; resultCount: number }[];
    popular: { query: string; type: 'popular'; count: number }[];
  }>({ user_history: [], popular: [] });
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);

  // Sucheinstellungen in sessionStorage speichern
  useEffect(() => {
    sessionStorage.setItem('stockSearch_query', JSON.stringify(query));
  }, [query]);

  useEffect(() => {
    sessionStorage.setItem('stockSearch_source', JSON.stringify(source));
  }, [source]);

  useEffect(() => {
    sessionStorage.setItem('stockSearch_orientation', JSON.stringify(orientation));
  }, [orientation]);

  useEffect(() => {
    sessionStorage.setItem('stockSearch_color', JSON.stringify(colorFilter));
  }, [colorFilter]);

  useEffect(() => {
    sessionStorage.setItem('stockSearch_photos', JSON.stringify(photos));
  }, [photos]);

  useEffect(() => {
    sessionStorage.setItem('stockSearch_page', JSON.stringify(page));
  }, [page]);

  useEffect(() => {
    sessionStorage.setItem('stockSearch_hasMore', JSON.stringify(hasMore));
  }, [hasMore]);

  // Load available sources on mount
  useEffect(() => {
    api.get<{ sources: string[] }>('/stock/sources')
      .then((res) => {
        setAvailableSources(res.sources || []);
      })
      .catch(() => {
        setAvailableSources([]);
      });
  }, []);

  // Fetch suggestions when input is focused or query changes
  const fetchSuggestions = useCallback(async (prefix?: string) => {
    setLoadingSuggestions(true);
    try {
      const params = new URLSearchParams();
      if (prefix) params.set('q', prefix);
      const response = await api.get<{
        user_history: { query: string; type: 'history'; resultCount: number }[];
        popular: { query: string; type: 'popular'; count: number }[];
      }>(`/stock/suggestions?${params}`);
      setSuggestions(response);
    } catch (err) {
      console.error('Failed to fetch suggestions:', err);
    } finally {
      setLoadingSuggestions(false);
    }
  }, []);

  // Debounced suggestion fetch
  const suggestionTimeoutRef = useRef<number | null>(null);
  useEffect(() => {
    if (!showSuggestions) return;

    if (suggestionTimeoutRef.current) {
      window.clearTimeout(suggestionTimeoutRef.current);
    }

    suggestionTimeoutRef.current = window.setTimeout(() => {
      fetchSuggestions(query || undefined);
    }, 200);

    return () => {
      if (suggestionTimeoutRef.current) {
        window.clearTimeout(suggestionTimeoutRef.current);
      }
    };
  }, [query, showSuggestions, fetchSuggestions]);

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        suggestionsRef.current &&
        !suggestionsRef.current.contains(e.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(e.target as Node)
      ) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handle suggestion click
  const handleSuggestionClick = (suggestionQuery: string) => {
    setQuery(suggestionQuery);
    setShowSuggestions(false);
    search(suggestionQuery, 1, false);
  };

  const search = useCallback(async (searchQuery: string, pageNum: number = 1, append: boolean = false) => {
    if (!searchQuery.trim()) return;

    setLoading(true);
    setError(null);
    setSuccessMessage(null);

    try {
      const params = new URLSearchParams({
        q: searchQuery,
        source,
        page: String(pageNum),
        per_page: '20',
      });
      if (orientation) {
        params.set('orientation', orientation);
      }
      if (colorFilter) {
        params.set('color', colorFilter);
      }

      const response = await api.get<{ results: StockPhoto[] }>(`/stock/search?${params}`);
      const newPhotos = response.results || [];

      if (append) {
        setPhotos((prev) => [...prev, ...newPhotos]);
      } else {
        setPhotos(newPhotos);
      }

      setHasMore(newPhotos.length === 20);
      setPage(pageNum);
    } catch (err: any) {
      setError(err.response?.data?.error || t('stock.searchFailed'));
      if (!append) setPhotos([]);
    } finally {
      setLoading(false);
    }
  }, [source, orientation, colorFilter]);

  // Suche zurücksetzen
  const resetSearch = () => {
    setQuery('');
    setSource(defaultSource);
    setOrientation('');
    setColorFilter('');
    setPhotos([]);
    setPage(1);
    setHasMore(false);
    setDownloadedIds(new Set());
    // SessionStorage leeren
    ['query', 'source', 'orientation', 'color', 'photos', 'page', 'hasMore'].forEach(key => {
      sessionStorage.removeItem(`stockSearch_${key}`);
    });
  };

  // Success Message mit Timer anzeigen
  const showSuccess = (message: string) => {
    if (successTimerRef.current) {
      window.clearTimeout(successTimerRef.current);
    }
    setSuccessMessage(message);
    successTimerRef.current = window.setTimeout(() => {
      setSuccessMessage(null);
      successTimerRef.current = null;
    }, 3000);
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setShowSuggestions(false);
    search(query, 1, false);
  };

  const handleLoadMore = () => {
    search(query, page + 1, true);
  };

  const openDownloadModal = (photo: StockPhoto) => {
    setDownloadPhoto(photo);
    setDownloadSize('regular');
    setSelectedFlightId('');
    setSelectedPostId('');
    setShowDownloadModal(true);
  };

  const handleDownload = async (photo: StockPhoto, size: DownloadSize = 'regular', flightId?: string, postId?: string) => {
    setDownloading(photo.id);
    setError(null);
    try {
      const response = await api.post<{ success: boolean; media: { id: string; url: string } }>('/stock/download', {
        source: photo.source,
        photo_id: photo.id,
        project_id: projectId,
        size,
        flight_id: flightId || undefined,
        post_id: postId || undefined,
      });

      if (response.success) {
        // Als heruntergeladen markieren
        setDownloadedIds(prev => new Set(prev).add(`${photo.source}-${photo.id}`));
        // Erfolg anzeigen
        showSuccess(t('stock.savedToLibrary', { photographer: photo.attribution.photographer }));
        // Callback aufrufen
        if (onDownload) {
          onDownload(response.media.id, response.media.url);
        }
      }
      setShowDownloadModal(false);
      setDownloadPhoto(null);
    } catch (err: any) {
      setError(err.response?.data?.error || t('stock.downloadFailed'));
    } finally {
      setDownloading(null);
    }
  };

  const handleQuickDownload = async (photo: StockPhoto) => {
    // Quick download without modal - just use regular size
    await handleDownload(photo, 'regular');
  };

  // Bei Filteränderung automatisch neu suchen, wenn bereits gesucht wurde
  // Aber nicht beim initialen Mount (wenn Daten aus sessionStorage kommen)
  useEffect(() => {
    if (isInitialMountRef.current) {
      isInitialMountRef.current = false;
      return;
    }
    if (query.trim() && photos.length > 0) {
      search(query, 1, false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [colorFilter]);

  const handleSelect = (photo: StockPhoto) => {
    setSelectedPhoto(photo);
    if (onSelect) {
      onSelect(photo);
    }
  };

  if (availableSources.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        <svg className="w-12 h-12 mx-auto mb-3 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
        <p className="font-medium">{t('stock.notConfigured')}</p>
        <p className="text-sm mt-1">{t('stock.notConfiguredHint')}</p>
      </div>
    );
  }

  return (
    <div className={compact ? '' : 'space-y-4'}>
      {/* Search Form */}
      <form onSubmit={handleSearch} className="space-y-3">
        <div className="flex gap-2">
          <div className="relative flex-1">
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onFocus={() => setShowSuggestions(true)}
              onKeyDown={(e) => {
                if (e.key === 'Escape') setShowSuggestions(false);
              }}
              placeholder={t('stock.searchPlaceholder')}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              autoComplete="off"
            />
            <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>

            {/* Autocomplete Dropdown */}
            {showSuggestions && (suggestions.user_history.length > 0 || suggestions.popular.length > 0) && (
              <div
                ref={suggestionsRef}
                className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-64 overflow-y-auto"
              >
                {/* User History */}
                {suggestions.user_history.length > 0 && (
                  <div className="p-2">
                    <div className="text-xs font-medium text-gray-500 px-2 mb-1">{t('stock.recentSearches')}</div>
                    {suggestions.user_history.map((item, idx) => (
                      <button
                        key={`history-${idx}`}
                        type="button"
                        onClick={() => handleSuggestionClick(item.query)}
                        className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100 rounded flex items-center gap-2"
                      >
                        <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span className="flex-1">{item.query}</span>
                        {item.resultCount > 0 && (
                          <span className="text-xs text-gray-400">{t('stock.resultsCount', { count: item.resultCount })}</span>
                        )}
                      </button>
                    ))}
                  </div>
                )}

                {/* Divider */}
                {suggestions.user_history.length > 0 && suggestions.popular.length > 0 && (
                  <div className="border-t border-gray-100" />
                )}

                {/* Popular Searches */}
                {suggestions.popular.length > 0 && (
                  <div className="p-2">
                    <div className="text-xs font-medium text-gray-500 px-2 mb-1">{t('stock.popular')}</div>
                    {suggestions.popular.map((item, idx) => (
                      <button
                        key={`popular-${idx}`}
                        type="button"
                        onClick={() => handleSuggestionClick(item.query)}
                        className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100 rounded flex items-center gap-2"
                      >
                        <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                        </svg>
                        <span className="flex-1">{item.query}</span>
                        <span className="text-xs text-gray-400">{t('stock.searchedCount', { count: item.count })}</span>
                      </button>
                    ))}
                  </div>
                )}

                {loadingSuggestions && (
                  <div className="p-3 text-center text-sm text-gray-500">
                    <svg className="animate-spin h-4 w-4 mx-auto" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                  </div>
                )}
              </div>
            )}
          </div>
          <button
            type="submit"
            disabled={loading || !query.trim()}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
              </svg>
            ) : (
              t('stock.search')
            )}
          </button>
        </div>

        {/* Filters */}
        {!compact && (
          <div className="space-y-3">
            {/* Quick Filters Row */}
            <div className="flex flex-wrap items-center gap-2">
              <select
                value={source}
                onChange={(e) => setSource(e.target.value as 'unsplash' | 'pexels' | 'all')}
                className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">{t('stock.allSources')}</option>
                {availableSources.includes('unsplash') && <option value="unsplash">Unsplash</option>}
                {availableSources.includes('pexels') && <option value="pexels">Pexels</option>}
              </select>

              <select
                value={orientation}
                onChange={(e) => setOrientation(e.target.value as 'landscape' | 'portrait' | 'square' | '')}
                className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
              >
                <option value="">{t('stock.allFormats')}</option>
                <option value="landscape">{t('stock.landscape')}</option>
                <option value="portrait">{t('stock.portrait')}</option>
                <option value="square">{t('stock.square')}</option>
              </select>

              {/* Color Filter Toggle */}
              <button
                type="button"
                onClick={() => setShowFilters(!showFilters)}
                className={`px-3 py-1.5 border rounded-lg text-sm flex items-center gap-1.5 transition-colors ${
                  showFilters || colorFilter
                    ? 'border-blue-500 bg-blue-50 text-blue-700'
                    : 'border-gray-300 hover:border-gray-400'
                }`}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
                </svg>
                {t('stock.color')}
                {colorFilter && (
                  <span className="w-3 h-3 rounded-full ml-1" style={{
                    backgroundColor: COLOR_OPTIONS.find(c => c.value === colorFilter)?.value === 'white' ? '#fff' :
                      COLOR_OPTIONS.find(c => c.value === colorFilter)?.value || '#888'
                  }} />
                )}
              </button>

              {/* Active Filter Count & Reset */}
              {(orientation || colorFilter || source !== 'all') && (
                <button
                  type="button"
                  onClick={resetSearch}
                  className="px-2 py-1.5 text-xs text-gray-500 hover:text-red-600 flex items-center gap-1"
                >
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                  {t('stock.resetFilters')}
                </button>
              )}
            </div>

            {/* Color Filter Panel */}
            {showFilters && (
              <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                <label className="block text-xs font-medium text-gray-500 mb-2">{t('stock.filterColor')}</label>
                <div className="flex flex-wrap gap-2">
                  {COLOR_OPTIONS.map((color) => (
                    <button
                      key={color.value}
                      type="button"
                      onClick={() => setColorFilter(color.value)}
                      className={`w-7 h-7 rounded-full transition-all ${color.color} ${
                        colorFilter === color.value
                          ? 'ring-2 ring-offset-2 ring-blue-500 scale-110'
                          : 'hover:scale-105'
                      }`}
                      title={t(color.labelKey)}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Popular Searches (nur wenn noch nicht gesucht wurde) */}
            {!query && photos.length === 0 && (
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-2">{t('stock.popularSearches')}</label>
                <div className="flex flex-wrap gap-2">
                  {POPULAR_SEARCHES.map((term) => (
                    <button
                      key={term}
                      type="button"
                      onClick={() => {
                        setQuery(term);
                        search(term, 1, false);
                      }}
                      className="px-3 py-1 text-sm bg-white border border-gray-200 rounded-full hover:border-blue-300 hover:bg-blue-50 transition-colors"
                    >
                      {term}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </form>

      {/* Success Message */}
      {successMessage && (
        <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm flex items-center gap-2">
          <svg className="w-5 h-5 text-green-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
          {successMessage}
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm flex items-center gap-2">
          <svg className="w-5 h-5 text-red-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          {error}
        </div>
      )}

      {/* Results - Masonry Layout */}
      {photos.length > 0 && (
        <>
          <div className="columns-2 sm:columns-3 md:columns-4 gap-3 space-y-3">
            {photos.map((photo) => (
                <div
                  key={`${photo.source}-${photo.id}`}
                  className={`group relative rounded-xl overflow-hidden cursor-pointer transition-all break-inside-avoid mb-3 shadow-sm hover:shadow-lg ${
                    selectedPhoto?.id === photo.id && selectedPhoto?.source === photo.source
                      ? 'ring-2 ring-blue-500 ring-offset-2'
                      : ''
                  }`}
                  style={{ backgroundColor: photo.color }}
                  onClick={() => handleSelect(photo)}
                >
                  <img
                    src={photo.urls.regular}
                    alt={photo.description}
                    className="w-full h-auto object-cover"
                    style={{
                      aspectRatio: `${photo.width} / ${photo.height}`
                    }}
                    loading="lazy"
                  />

                  {/* Downloaded indicator badge */}
                  {downloadedIds.has(`${photo.source}-${photo.id}`) && (
                    <div className="absolute top-2 right-2 bg-green-500 text-white rounded-full p-1.5 shadow-lg z-10">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                  )}

                  {/* Overlay on hover */}
                  <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-all flex items-end">
                    <div className="w-full p-2 translate-y-full group-hover:translate-y-0 transition-transform">
                      {/* Source badge */}
                      <div className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium mb-1 ${
                        photo.source === 'unsplash' ? 'bg-black text-white' : 'bg-teal-500 text-white'
                      }`}>
                        {photo.source === 'unsplash' ? 'Unsplash' : 'Pexels'}
                      </div>

                      {/* Photographer */}
                      <div className="text-white text-xs truncate">
                        {photo.attribution.photographer}
                      </div>

                      {/* Actions */}
                      <div className="flex gap-1 mt-1">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleQuickDownload(photo);
                          }}
                          disabled={downloading === photo.id}
                          className={`flex-1 px-2 py-1 text-xs rounded disabled:opacity-50 ${
                            downloadedIds.has(`${photo.source}-${photo.id}`)
                              ? 'bg-green-500 text-white hover:bg-green-600'
                              : 'bg-white text-gray-800 hover:bg-gray-100'
                          }`}
                          title={downloadedIds.has(`${photo.source}-${photo.id}`)
                            ? t('stock.saveAgain')
                            : t('stock.quickSave')}
                        >
                          {downloading === photo.id ? t('stock.loading') :
                            downloadedIds.has(`${photo.source}-${photo.id}`) ? t('stock.saved') : t('stock.save')}
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            openDownloadModal(photo);
                          }}
                          className="px-2 py-1 bg-white/90 text-gray-800 text-xs rounded hover:bg-white"
                          title={t('stock.saveOptions')}
                        >
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
                          </svg>
                        </button>
                        <a
                          href={photo.attribution.source_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="px-2 py-1 bg-white/80 text-gray-800 text-xs rounded hover:bg-white"
                          title={t('stock.viewOriginal')}
                        >
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                          </svg>
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
            ))}
          </div>

          {/* Load More */}
          {hasMore && (
            <div className="text-center pt-4">
              <button
                onClick={handleLoadMore}
                disabled={loading}
                className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
              >
                {loading ? t('stock.loading') : t('stock.loadMore')}
              </button>
            </div>
          )}

          {/* Attribution notice */}
          <div className="text-xs text-gray-500 text-center pt-2">
            {t('stock.attribution')}{' '}
            {availableSources.includes('unsplash') && (
              <a href="https://unsplash.com" target="_blank" rel="noopener noreferrer" className="underline hover:text-gray-700">
                Unsplash
              </a>
            )}
            {availableSources.length > 1 && ' & '}
            {availableSources.includes('pexels') && (
              <a href="https://pexels.com" target="_blank" rel="noopener noreferrer" className="underline hover:text-gray-700">
                Pexels
              </a>
            )}
          </div>
        </>
      )}

      {/* Empty state */}
      {!loading && query && photos.length === 0 && (
        <div className="text-center py-8 text-gray-500">
          <svg className="w-12 h-12 mx-auto mb-3 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <p>{t('stock.noResults', { query })}</p>
          <p className="text-sm mt-1">{t('stock.noResultsHint')}</p>
        </div>
      )}

      {/* Initial state */}
      {!loading && !query && photos.length === 0 && (
        <div className="text-center py-8 text-gray-500">
          <svg className="w-12 h-12 mx-auto mb-3 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
          <p>{t('stock.initialState')}</p>
          <p className="text-sm mt-1">{t('stock.initialStateHint')}</p>
        </div>
      )}

      {/* Download Modal */}
      {showDownloadModal && downloadPhoto && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowDownloadModal(false)}>
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full mx-4 overflow-hidden" onClick={(e) => e.stopPropagation()}>
            {/* Header with preview */}
            <div className="relative h-48 bg-gray-100">
              <img
                src={downloadPhoto.urls.regular}
                alt={downloadPhoto.description}
                className="w-full h-full object-cover"
              />
              <button
                onClick={() => setShowDownloadModal(false)}
                className="absolute top-2 right-2 p-1.5 bg-black/50 hover:bg-black/70 rounded-full text-white"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
              <div className={`absolute bottom-2 left-2 px-2 py-1 rounded text-xs font-medium ${
                downloadPhoto.source === 'unsplash' ? 'bg-black text-white' : 'bg-teal-500 text-white'
              }`}>
                {downloadPhoto.source === 'unsplash' ? 'Unsplash' : 'Pexels'}
              </div>
            </div>

            {/* Content */}
            <div className="p-4 space-y-4">
              <div className="text-sm text-gray-600">
                {t('stock.photoBy')} <span className="font-medium text-gray-900">{downloadPhoto.attribution.photographer}</span>
              </div>

              {/* Size selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">{t('stock.imageSize')}</label>
                <div className="grid grid-cols-3 gap-2">
                  {([
                    { value: 'small', labelKey: 'stock.sizeSmall', descKey: 'stock.sizeSmallDesc' },
                    { value: 'regular', labelKey: 'stock.sizeMedium', descKey: 'stock.sizeMediumDesc' },
                    { value: 'full', labelKey: 'stock.sizeOriginal', descKey: 'stock.sizeOriginalDesc' },
                  ] as const).map((size) => (
                    <button
                      key={size.value}
                      onClick={() => setDownloadSize(size.value)}
                      className={`p-3 rounded-lg border-2 transition-all text-left ${
                        downloadSize === size.value
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="font-medium text-sm">{t(size.labelKey)}</div>
                      <div className="text-xs text-gray-500">{t(size.descKey)}</div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Flight selection */}
              {flights.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('stock.assignFlightOptional')}</label>
                  <select
                    value={selectedFlightId}
                    onChange={(e) => setSelectedFlightId(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                  >
                    <option value="">{t('stock.noFlightAssign')}</option>
                    {flights.map((flight) => (
                      <option key={flight.id} value={flight.id}>
                        {flight.name} ({flight.display_id})
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Post selection */}
              {posts.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('stock.assignPostOptional')}</label>
                  <select
                    value={selectedPostId}
                    onChange={(e) => setSelectedPostId(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                  >
                    <option value="">{t('stock.noPostAssign')}</option>
                    {posts.map((post) => (
                      <option key={post.id} value={post.id}>
                        {post.display_id}: {post.text.slice(0, 50)}{post.text.length > 50 ? '...' : ''}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="px-4 py-3 bg-gray-50 flex justify-end gap-2">
              <button
                onClick={() => setShowDownloadModal(false)}
                className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg"
              >
                {t('common:cancel')}
              </button>
              <button
                onClick={() => handleDownload(
                  downloadPhoto,
                  downloadSize,
                  selectedFlightId || undefined,
                  selectedPostId || undefined
                )}
                disabled={downloading === downloadPhoto.id}
                className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
              >
                {downloading === downloadPhoto.id ? (
                  <>
                    <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    {t('stock.saving')}
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                    </svg>
                    {t('stock.saveToLibrary')}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export type { StockPhoto, Flight as StockFlight, Post as StockPost };
